// Timeline & Facts API surface (Timeline & Facts spec). Built on the shared request
// helper in api/client.ts — never raw fetch in components.
//
// Three layers, one stream: Layer 0 (documents ARE entries — derived, zero curation),
// Layer 1 (timeline_events — curated happenings), Layer 2 (client_facts — "latest known
// instance of a fact" with as-of + mandatory source + supersession chain).
import { request } from './client.js';
import type { RunRow } from './agent.js';
import type { DocStatus, DocumentFolder, SourceType } from './documents.js';

export const EVENT_KINDS = [
  'meeting', 'decision', 'signature', 'commitment', 'milestone', 'participant', 'note',
] as const;
export type EventKind = (typeof EVENT_KINDS)[number];

export interface Participant {
  name: string;
  email?: string;
  contact_id?: number;
}

/** A document anchored to an event (event_documents row, joined). */
export interface EventDocumentLink {
  id: number;
  ref: string;
  original_filename: string;
  folder: DocumentFolder | null;
}

interface EntryBase {
  id: number;
  /** documents = the quiet derived layer; events = the prominent curated one. */
  layer: 'document' | 'event';
  /** Merge key — effective_date for documents, occurred_at for events. Null sorts last. */
  date: string | null;
  time: string | null;
}

export interface DocumentEntry extends EntryBase {
  layer: 'document';
  ref: string;
  original_filename: string;
  effective_date: string | null;
  effective_date_confirmed: boolean;
  folder: DocumentFolder | null;
  tags: string[];
  authored_by: string | null;
  ai_summary: string | null;
  source_type: SourceType;
  status: DocStatus;
  superseded_by_document_id: number | null;
  superseded_by_ref: string | null;
  /** Reverse pointer — the ref this document supersedes (v-chain rendering). */
  supersedes_ref: string | null;
  engagement_codes: string[];
}

export interface EventEntry extends EntryBase {
  layer: 'event';
  ref: string; // TE-nnn, per client
  kind: EventKind;
  occurred_at: string;
  occurred_time: string | null;
  title: string;
  summary: string | null;
  participants: Participant[];
  /** Commitments only: whose the promise is. Required at creation — never inferred. */
  commitment_owner: 'us' | 'them' | null;
  commitment_due: string | null;
  /** The monotonic "promise was kept" flip. Null = the ball is still in someone's court. */
  discharged_at: string | null;
  discharge_note: string | null;
  /** A discharge carries evidence — exactly one of these is set when discharged_at is. */
  discharged_by_document_ref: string | null;
  discharged_by_event_ref: string | null;
  engagement_id: number | null;
  engagement_code: string | null;
  created_by_kind: 'operator' | 'agent';
  created_by: number | null;
  created_at: string;
  documents: EventDocumentLink[];
}

export type TimelineEntry = DocumentEntry | EventEntry;

export interface TimelineResponse {
  client_id: number;
  engagement_id: number | null;
  from: string | null;
  to: string | null;
  count: number;
  documents: number;
  events: number;
  entries: TimelineEntry[];
}

export type FactStatus = 'current' | 'superseded' | 'retracted';

export interface FactRow {
  id: number;
  key: string;
  value: string;
  as_of: string | null;
  stated_by: string | null;
  quote: string | null;
  supersedes_fact_id: number | null;
  /** Novel (off-catalog) key accepted via novel:true — rendered under "Client-specific". */
  uncatalogued: boolean;
  confirmed_by: number | null;
  confirmed_at: string | null;
  status: FactStatus;
  created_by_kind: 'operator' | 'agent';
  created_by: number | null;
  created_at: string;
  source_document_id: number;
  source_document_ref: string;
  source_document_filename: string;
}

/** One key's state: the current value plus its supersession/retraction history. */
export interface FactGroup {
  key: string;
  current: FactRow | null;
  history_count: number;
  history?: FactRow[];
}

/** One standing field of the fact catalog. Exactly one of key/family is set; families take
 *  '<stem>.<instance>' keys where the suffix names the instance. */
export interface FactCatalogSlot {
  key?: string;
  family?: string;
  instance?: string;
  label: string;
  description: string;
}

/** One prefix of the standing catalog, slots in render order (server is the source of truth). */
export interface FactCatalogPrefix {
  prefix: string;
  description: string;
  /** The conversational group this prefix's facts render under (Money · Timing · …). */
  group: string;
  slots: FactCatalogSlot[];
}

export interface FactsResponse {
  client_id: number;
  count: number;
  facts: FactGroup[];
  /** The standing catalog — ships with the facts so the rail renders labels + empty slots. */
  catalog: FactCatalogPrefix[];
}

export interface EventInput {
  client_id: number;
  engagement_id?: number | null;
  kind: EventKind;
  occurred_at: string;
  occurred_time?: string | null;
  title: string;
  summary?: string | null;
  participants?: Participant[];
  document_ids?: number[];
}

export type EventPatch = Partial<Omit<EventInput, 'client_id'>>;

/** The discharge verb (INTEGRATION §3.1): a date plus the evidence that kept the promise. */
export interface DischargeInput {
  discharged_at: string;
  discharged_by_document_ref?: string;
  discharged_by_event_ref?: string;
  discharge_note?: string | null;
}

export interface FactCreateInput {
  client_id: number;
  key: string;
  value: string;
  as_of?: string | null;
  stated_by?: string | null;
  source_document_id?: number;
  document_ref?: string;
  quote?: string | null;
  /** Standing fields have priority: an off-catalog key is rejected unless novel is true. */
  novel?: boolean;
}

export interface FactPatch {
  value?: string;
  as_of?: string | null;
  stated_by?: string | null;
  quote?: string | null;
  source_document_id?: number;
  document_ref?: string;
  key?: string;
  /** Required alongside an off-catalog `key` — same priority rule as create. */
  novel?: boolean;
  /** Durable operator confirmation of an agent-recorded fact — sets confirmed_by/at. */
  confirm?: boolean;
  /** 'superseded' is written by the chain, never by hand. */
  status?: 'current' | 'retracted';
}

/** The reconstruct run row — hosted on one engagement, reads the WHOLE client library. */
export interface ReconstructRun extends RunRow {
  client_id: number;
  hosted_engagement_id: number;
}

function qs(params: Record<string, unknown>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '' || value === false) continue;
    search.set(key, String(value));
  }
  const out = search.toString();
  return out ? `?${out}` : '';
}

const json = (method: string, body: unknown): RequestInit => ({ method, body: JSON.stringify(body) });

export const timelineApi = {
  /** The merged chronological stream (documents + events). Filters are server-side. */
  stream: (clientId: number, filters: { engagement_id?: number; from?: string; to?: string } = {}) =>
    request<TimelineResponse>(`/timeline${qs({ client_id: clientId, ...filters })}`),

  /** "What we currently know" — current facts grouped by key, history on request. */
  facts: (clientId: number, opts: { key?: string; history?: boolean } = {}) =>
    request<FactsResponse>(`/timeline/facts${qs({ client_id: clientId, key: opts.key, history: opts.history })}`),

  createEvent: (input: EventInput) => request<EventEntry>('/timeline/events', json('POST', input)),

  updateEvent: (id: number, patch: EventPatch) =>
    request<EventEntry>(`/timeline/events/${id}`, json('PATCH', patch)),

  /** Close an open commitment. 422 carries the corrective message; 409 = already discharged. */
  discharge: (id: number, input: DischargeInput) =>
    request<EventEntry>(`/timeline/events/${id}`, json('PATCH', input)),

  deleteEvent: (id: number) =>
    request<{ deleted: number; ref: string }>(`/timeline/events/${id}`, { method: 'DELETE' }),

  /** Same auto-supersede write path as the agent tool — 422 when the quote is not verbatim. */
  createFact: (input: FactCreateInput) =>
    request<FactRow & { superseded_fact_id: number | null }>('/timeline/facts', json('POST', input)),

  updateFact: (id: number, patch: FactPatch) => request<FactRow>(`/timeline/facts/${id}`, json('PATCH', patch)),

  /** Deleting the current node resurrects its predecessor — the undo for a wrong latest value. */
  deleteFact: (id: number) =>
    request<{ deleted: number; key: string; restored_fact_id: number | null }>(
      `/timeline/facts/${id}`, { method: 'DELETE' },
    ),

  /** One-click reconstruction pass. Returns the run immediately — poll agentApi.getRun. */
  reconstruct: (clientId: number, engagementId?: number | null) =>
    request<ReconstructRun>('/timeline/reconstruct', json('POST', {
      client_id: clientId,
      ...(engagementId ? { engagement_id: engagementId } : {}),
    })),
};

export default timelineApi;
